import { getQueryString, handleError, page_size } from "../apiUtils";
import { createAxiosInstance } from "../apiUtils";
import { AxiosRequestConfig } from "axios";
import { refreshRoutesAndConnections } from "../cacheRefresh";

export async function getRoutesList(token: string, query: object) {
    const axiosInstance = await createAxiosInstance(token);
    const query_string = getQueryString(query)
    try {
        const resp = await axiosInstance.get("/v2/connections/?pager=limit&limit=" + page_size + "&" + query_string);
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function getRouteProviderTypes(token: string, query?: object, signal?: AbortSignal) {
    const axiosInstance = await createAxiosInstance(token);
    const query_string = getQueryString({ limit: page_size, ...(query || {}) })
    try {
        const resp = await axiosInstance.get("/v2/integrations/types/?is_provider=true&pager=limit&" + query_string, { signal });
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// Bidirectional flows (GUNDI-5595): the reverse direction is a separate Route whose
// provider is one of this Route's destinations and whose destination is this
// Route's provider. `?destination_id=` is the only filter that can find it —
// there is no provider_id filter on /v2/connections/.
export async function getReverseRoutes(token: string, destination_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/connections/?destination_id=" + destination_id);
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export type IntegrationRoles = {
    asProvider: { id: string, name: string }[];
    asDestination: { id: string, name: string }[];
};

// Every Route, one page at a time, inverted into "which role does each
// integration play, and in what". No endpoint carries per-integration role data, so
// the Connections list derives it here once and reuses it for every row
// instead of asking per record.
export async function loadIntegrationRoleMap(token: string): Promise<Record<string, IntegrationRoles>> {
    const map: Record<string, IntegrationRoles> = {};
    const bucket = (id: string) => (map[id] = map[id] || { asProvider: [], asDestination: [] });
    let offset = 0;
    // Bounded rather than trusting `next` forever.
    for (let page = 0; page < 50; page += 1) {
        const data = await getRoutesList(token, { offset });
        const results: any[] = data?.results || [];
        results.forEach((route: any) => {
            const label = { id: route.id, name: route.provider?.name || route.id };
            if (route.provider?.id) bucket(route.provider.id).asProvider.push(label);
            (route.destinations || []).forEach((d: any) => {
                if (d?.id) bucket(d.id).asDestination.push(label);
            });
        });
        if (!data?.next || results.length === 0) return map;
        offset += results.length;
    }
    // Hit the page cap with pages still to go: the map is partial, so some records
    // will look less used than they are. Say so rather than failing silently.
    console.log("route role map truncated at 50 pages; some roles may be missing");
    return map;
}

// Creates the reverse-direction route. `data_providers` accepts EXISTING integration
// ids, so the same two integration records serve both directions (GUNDI-5595) — no
// duplicate provider record is created. Note this does not touch integration.default_route.
export async function createRoute(token: string, payload: {
    name: string,
    owner: string,
    data_providers: string[],
    destinations: string[],
}) {
    const axiosInstance = await createAxiosInstance(token);
    const resp = await axiosInstance.post("/v2/routes/", payload);
    refreshRoutesAndConnections();
    return resp.data;
}

export async function deleteRoute(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    const resp = await axiosInstance.delete("/v2/routes/" + route_id + "/");
    refreshRoutesAndConnections();
    return resp.data;
}

// A route record is self-sufficient: data_providers and destinations come back
// as full integration summaries (id, name, owner, type, base_url, status), and
// configuration carries the field mappings. Routes are addressed by their own
// id — decoupled from the provider Connection's id.
export async function getRoute(token: string, route_id: string) {
    try {
        const axiosInstance = await createAxiosInstance(token);
        const resp = await axiosInstance.get("/v2/routes/" + route_id + "/");
        return resp.data;
    } catch (err: any) {
        if (err?.response?.status === 404 || err?.response?.status === 403) throw err;
        handleError(err);
    }
}

// Every route where the integration plays the given role. Backed by RouteFilter's
// ?provider= / ?destination= params (each matches data_providers__id / destinations__id).
// `ok` distinguishes "this integration has no routes" from "we could not find
// out" — callers that offer to CREATE a route must not treat a failed lookup as
// "none exist" (that is how duplicates get made).
export async function getRoutesByIntegration(
    token: string,
    role: "provider" | "destination",
    integration_id: string,
    query: object = {},
): Promise<{ results: any[], count: number, ok: boolean }> {
    try {
        const axiosInstance = await createAxiosInstance(token);
        // Same explicit paging as every other list endpoint: relying on the
        // backend default would desynchronise offsets from the page size the
        // tables render.
        const resp = await axiosInstance.get("/v2/routes/", {
            params: { [role]: integration_id, pager: "limit", limit: page_size, ...query },
        });
        return { results: resp.data?.results ?? [], count: resp.data?.count ?? 0, ok: true };
    } catch (err) {
        // Deliberately not handleError: it rethrows everything but a 401, which
        // would break this function's no-throw contract.
        console.error("failed to list routes by integration", role, integration_id, err);
        return { results: [], count: 0, ok: false };
    }
}

// Route records for a picker: unlike /v2/connections/ (one entry per provider,
// with routing_rules reduced to {id, name}), each record carries its OWN name,
// destinations and data_providers — which is what makes per-Route identity and
// eligibility possible. `search` is supported by the endpoint's search fields
// (name, owner name, provider and destination names/urls).
export async function listRoutes(token: string, query: object = {}): Promise<{ results: any[], next: any, ok: boolean }> {
    try {
        const axiosInstance = await createAxiosInstance(token);
        const resp = await axiosInstance.get("/v2/routes/", {
            params: { pager: "limit", limit: page_size, ...query },
        });
        return { results: resp.data?.results ?? [], next: resp.data?.next ?? null, ok: true };
    } catch (err) {
        // No handleError: it rethrows everything but a 401, and callers rely on
        // this resolving.
        console.error("failed to list routes", err);
        return { results: [], next: null, ok: false };
    }
}

// The Route's own name lives on the route record, not on the provider integration —
// renaming a Route must not rename the Connection that provides its data.
export async function renameRoute(token: string, route_id: string, name: string) {
    const axiosInstance = await createAxiosInstance(token);
    const resp = await axiosInstance.patch("/v2/routes/" + route_id + "/", { name });
    refreshRoutesAndConnections();
    return resp.data;
}

// Which of these integrations have an action of the given type? Used to gate the
// "enable reverse direction" toggles: a site can only send data back if its type
// registers a pull action. One call instead of N integration lookups.
export async function getIntegrationIdsWithActionType(token: string, ids: string[], actionType: string) {
    if (ids.length === 0) return [];
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get(`/v2/integrations/?id__in=${ids.join(",")}&action_type=${actionType}`);
        return (resp.data?.results || resp.data || []).map((i: any) => i.id);
    } catch (err) {
        console.log("capability lookup failed", err);
        return [];
    }
}

export async function getRouteDetails(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/connections/" + route_id + "/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function getRouteApiKey(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/integrations/" + route_id + "/api-key/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function getIntegrationDetails(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/integrations/" + route_id + "/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function getRouteLog(token: string, query: object, signal?: AbortSignal, timeout?: number) {
    const axiosInstance = await createAxiosInstance(token);
    const query_string = getQueryString(query)
    try {
        const config: AxiosRequestConfig = {};
        if (signal) {
            config.signal = signal;
        }
        if (timeout !== undefined) {
            config.timeout = timeout;
        }
        const resp = await axiosInstance.get("/v2/logs/?pager=cursor&" + query_string, config);
        return resp.data;
    } catch (err: any) {
        // Abort and timeout are expected cases; let the caller handle them directly.
        if (err && typeof err === "object") {
            const errorCode = (err as any).code;
            const errorName = (err as any).name;
            if (errorName === "AbortError" || errorCode === "ERR_CANCELED" || errorCode === "ECONNABORTED") {
                throw err;
            }
        }
        handleError(err);
    }
}

export async function getLogDetails(token: string, log_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/logs/" + log_id + "/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function createNewRoute(token: string, new_route: object) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.post("/v2/integrations/", new_route);
        refreshRoutesAndConnections();
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// Attach/detach destinations on a Route by PATCHing the underlying route with
// `{ destinations: [...] }`. Despite the endpoint living under `/v2/routes/`, this is
// scoped to destination membership only — see the `// TRANSFORMATION RULES` section
// below for the functions that PATCH the route's `configuration.data`.
export async function addRouteDestinations(token: string, route_id: string, payload: { destinations: string[] }) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.patch("/v2/routes/" + route_id + "/", payload);
        refreshRoutesAndConnections();
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// TRANSFORMATION RULES (route configuration)
// Field mappings live in `route.configuration.data.field_mappings`. The endpoint is the
// same `/v2/routes/{id}/` used for destination membership, but the payload shape is
// different: `{ configuration: { data: {...} } }`.

export async function getRouteConfiguration(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/routes/" + route_id + "/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// The backend updates the route_configuration linked to this route in place. We don't
// need to pass `configuration.id` — the route already references its configuration —
// and `data` is replaced wholesale, so callers must build the full data they want
// persisted (see fieldMappingTypes.buildConfigWithRule for the merge helper). 400
// validation errors propagate via the thrown axios error so the caller can surface
// them on the right field.
export async function updateRouteConfiguration(
    token: string,
    route_id: string,
    payload: { name: string; data: any }
) {
    const axiosInstance = await createAxiosInstance(token);
    // We intentionally rethrow so the caller can inspect 400 response bodies for
    // field-level validation errors. Other errors still go through handleError.
    const resp = await axiosInstance.patch("/v2/routes/" + route_id + "/", {
        configuration: payload,
    });
    refreshRoutesAndConnections();
    return resp.data;
}

// Unlinks the configuration from the route and hard-deletes it from the DB when
// no other route references it. Use this when the user removes the last rule —
// otherwise you'd PATCH the route with empty data and leave an orphan row behind.
// Idempotent: if the route has no configuration the backend still returns 204.
export async function deleteRouteConfiguration(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        await axiosInstance.delete("/v2/routes/" + route_id + "/configuration/");
        refreshRoutesAndConnections();
    } catch (err) {
        handleError(err);
    }
}

export async function updateConnection(token: string, integration_id: string, newConfigs: any) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.patch("/v2/integrations/" + integration_id + "/", newConfigs);
        refreshRoutesAndConnections();
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function testConnectionAction(token: string, integration_id: string, action_id: string, data: any) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.post("/v2/integrations/" + integration_id + "/actions/" + action_id + "/execute/", data);
        return { data: resp.data, status: resp.status };
    } catch (err) {
        handleError(err);
    }
}